import moment from 'moment-timezone'
import { formatClock } from './aiAgentOverview'

/**
 * Pure helpers for the Availability section of AI Agent Settings: turning the
 * `scheduling-data` response (availability records, providers, slots) into a
 * week a practice can read without knowing what an operatory column is.
 */

/** Today on the practice's own calendar, or the viewer's when the location has no timezone. */
export function practiceToday(timezone?: string | null): string {
  const now = timezone ? moment.tz(timezone) : moment()
  return now.format(`YYYY-MM-DD`)
}

export const DAY_KEYS = [`monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`, `sunday`] as const
export type DayKey = (typeof DAY_KEYS)[number]

type HoursRange = { from: string; to: string }
type DayTemplate = { open?: boolean | null; hours?: HoursRange[] | null } | null | undefined

/** One availability record as `scheduling-data` returns it (multi-operatory records already split). */
export type AvailabilityRecord = {
  id?: string | null
  providerId?: string | null
  operatoryId?: string | null
  appointmentTypeIds?: string[] | null
  backupAvailability?: boolean | null
  timeSlots?: Partial<Record<DayKey, DayTemplate>> | null
  /** Standard-mode records are synthesized per date and apply to that date only. */
  specificDate?: string | null
}

export type NameLookups = {
  providerName: (providerId: string | null | undefined) => string
  operatoryName: (operatoryId: string | null | undefined) => string
  /** Appointment type vendor id to the name the practice uses. Unknown ids are dropped. */
  serviceName: (vendorId: string) => string | undefined
}

export type SetupBlock = {
  key: string
  from: string
  to: string
  label: string
  provider: string
  operatory: string
  services: string[]
  backup: boolean
  /** Vendor ids of the appointment types the block can take; Live blocks carry it for the service filter. */
  typeIds?: string[]
}

/** The seven dates (YYYY-MM-DD) of the week that holds `anchor`, Monday first. */
export function weekDates(anchor: string): string[] {
  const monday = moment(anchor, `YYYY-MM-DD`).isoWeekday(1)
  return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, `days`).format(`YYYY-MM-DD`))
}

/** The anchor date for a week `offset` weeks after the current one. */
export function weekAnchor(offset: number, today = moment()): string {
  return today.clone().add(offset, `weeks`).format(`YYYY-MM-DD`)
}

export function weekLabel(dates: string[]): string {
  const first = moment(dates[0], `YYYY-MM-DD`)
  const last = moment(dates[dates.length - 1], `YYYY-MM-DD`)
  const sameMonth = first.isSame(last, `month`)
  return `${first.format(`MMM D`)} to ${last.format(sameMonth ? `D` : `MMM D`)}`
}

/**
 * The `day` parameters that cover a week. `scheduling-data` answers from the
 * given day to the end of its month, so a week that crosses a month boundary
 * needs the first of the next month as well. Days before the current month are
 * never answered, so a past week asks from today instead.
 */
export function requestDaysForWeek(dates: string[], today = moment()): string[] {
  const todayKey = today.format(`YYYY-MM-DD`)
  const start = dates[0] < todayKey ? todayKey : dates[0]
  const end = dates[dates.length - 1]
  if (end < start) return [start]
  const days = [start]
  const nextMonth = moment(start, `YYYY-MM-DD`).add(1, `month`).startOf(`month`)
  if (nextMonth.format(`YYYY-MM-DD`) <= end) days.push(nextMonth.format(`YYYY-MM-DD`))
  return days
}

function dayKeyOf(date: string): DayKey {
  return DAY_KEYS[moment(date, `YYYY-MM-DD`).isoWeekday() - 1]
}

function sortKey(time: string): number {
  const [h, m] = time.split(`:`).map(Number)
  return h * 60 + (m || 0)
}

/** The blocks a configuration opens on one date, earliest first. Dates before today draw nothing. */
export function setupBlocksForDate(records: AvailabilityRecord[], date: string, names: NameLookups): SetupBlock[] {
  const key = dayKeyOf(date)
  const blocks: SetupBlock[] = []
  for (const record of records) {
    if (record.specificDate && record.specificDate !== date) continue
    const template = record.timeSlots?.[key]
    if (!template?.open) continue
    for (const range of template.hours ?? []) {
      blocks.push({
        key: `${record.id ?? record.providerId}-${record.operatoryId}-${range.from}`,
        from: range.from,
        to: range.to,
        label: `${formatClock(range.from)} to ${formatClock(range.to)}`,
        provider: names.providerName(record.providerId),
        operatory: names.operatoryName(record.operatoryId),
        services: servicesOf(record, names),
        backup: !!record.backupAvailability,
      })
    }
  }
  // Two records for the same provider, room and hours read as one block.
  const merged = new Map<string, SetupBlock>()
  for (const block of blocks) {
    const key = `${block.provider}|${block.operatory}|${block.from}|${block.to}`
    const existing = merged.get(key)
    if (!existing) {
      merged.set(key, { ...block, key })
      continue
    }
    existing.services = [...new Set([...existing.services, ...block.services])].sort((a, b) => a.localeCompare(b))
    existing.backup = existing.backup && block.backup
  }
  return [...merged.values()].sort((a, b) => sortKey(a.from) - sortKey(b.from) || a.provider.localeCompare(b.provider))
}

function servicesOf(record: AvailabilityRecord, names: NameLookups): string[] {
  const seen = new Set<string>()
  for (const vendorId of record.appointmentTypeIds ?? []) {
    const name = names.serviceName(vendorId)
    if (name) seen.add(name)
  }
  return [...seen].sort((a, b) => a.localeCompare(b))
}

/** Days in one stretch of the upcoming-openings grid: two weeks, one column a day. */
export const OPENINGS_SPAN_DAYS = 14

/**
 * A rolling stretch of `OPENINGS_SPAN_DAYS` days from today, stepped whole stretches ahead, in the
 * shape the agent availability fetch takes. Rolling from today so the grid never spends a column on
 * a day already gone.
 */
export function openingsWindow(offset: number, today = moment()) {
  const start = today
    .clone()
    .startOf(`day`)
    .add(offset * OPENINGS_SPAN_DAYS, `days`)
  const end = start.clone().add(OPENINGS_SPAN_DAYS - 1, `days`)
  return {
    startISO: start.format(`YYYY-MM-DD`),
    endISO: end.format(`YYYY-MM-DD`),
    label: `${start.format(`MMM D`)} to ${end.format(`MMM D`)}`,
    monthName: start.format(`MMMM`),
    offset,
  }
}

/** The PMS's "Last, First" as "First Last"; any other non-empty name as it is. */
export function readableProviderName(pmsName: string | null | undefined): string | undefined {
  const name = pmsName?.trim()
  if (!name) return undefined
  const [last, first] = name.split(`,`).map((part) => part.trim())
  return first ? `${first} ${last}` : name
}

/** Sunday first, as the Calls by Time of Day heatmap reads. */
const SUNDAY_FIRST: DayKey[] = [`sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`]
const DAY_SHORT: Record<DayKey, string> = {
  sunday: `Sun`,
  monday: `Mon`,
  tuesday: `Tue`,
  wednesday: `Wed`,
  thursday: `Thu`,
  friday: `Fri`,
  saturday: `Sat`,
}

/** Weekdays as runs, Sunday first: "Mon–Fri", "Mon, Wed, Fri", "Tue–Thu, Sat", "Every day". */
export function formatDayRuns(days: DayKey[]): string {
  const on = SUNDAY_FIRST.map((day) => days.includes(day))
  if (on.every(Boolean)) return `Every day`
  const runs: string[] = []
  let start = -1
  for (let i = 0; i <= on.length; i++) {
    if (on[i] && start < 0) start = i
    if (!on[i] && start >= 0) {
      const end = i - 1
      const first = DAY_SHORT[SUNDAY_FIRST[start]]
      const last = DAY_SHORT[SUNDAY_FIRST[end]]
      runs.push(end - start >= 2 ? `${first}–${last}` : end === start ? first : `${first}, ${last}`)
      start = -1
    }
  }
  return runs.join(`, `)
}

/** "8:00 AM" and "5:30 PM" as "8 AM–5:30 PM", for a tight column. */
function compactRange(from: string, to: string): string {
  const short = (time: string) => formatClock(time).replace(`:00`, ``)
  return `${short(from)}–${short(to)}`
}

/** One provider who can take an appointment type: where, on which weekdays, and between which hours. */
export type TypeSetupProvider = {
  name: string
  rooms: string[]
  days: DayKey[]
  /** Earliest start to latest end across those days, e.g. "8 AM–5 PM". */
  hours?: string
  /** Every record they take it in is held back, so the agent offers it only on a second ask. */
  backupOnly: boolean
}

/**
 * Who the configuration lets take one appointment type (by vendor id), grouped by provider, from
 * the availability records `scheduling-data` returns. Records synthesized for a date count for that
 * date's weekday. With `activeProviderIds`, records for anyone else are left out.
 */
export function typeSetup(
  records: AvailabilityRecord[],
  vendorId: string,
  names: NameLookups,
  activeProviderIds?: Set<string>,
): TypeSetupProvider[] {
  const byProvider = new Map<
    string,
    { rooms: Set<string>; days: Set<DayKey>; from?: string; to?: string; backup: boolean }
  >()
  for (const record of records) {
    if (!record.appointmentTypeIds?.includes(vendorId)) continue
    if (activeProviderIds && (!record.providerId || !activeProviderIds.has(record.providerId))) continue
    const days = record.specificDate
      ? [dayKeyOf(record.specificDate)]
      : DAY_KEYS.filter((day) => record.timeSlots?.[day]?.open)
    const open = days.filter((day) => record.timeSlots?.[day]?.open)
    if (open.length === 0) continue
    const name = names.providerName(record.providerId)
    const entry = byProvider.get(name) ?? { rooms: new Set(), days: new Set(), backup: true }
    entry.rooms.add(names.operatoryName(record.operatoryId))
    entry.backup = entry.backup && !!record.backupAvailability
    for (const day of open) {
      entry.days.add(day)
      for (const range of record.timeSlots?.[day]?.hours ?? []) {
        if (!entry.from || sortKey(range.from) < sortKey(entry.from)) entry.from = range.from
        if (!entry.to || sortKey(range.to) > sortKey(entry.to)) entry.to = range.to
      }
    }
    byProvider.set(name, entry)
  }
  return [...byProvider.entries()]
    .map(([name, entry]) => ({
      name,
      rooms: [...entry.rooms].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      days: DAY_KEYS.filter((day) => entry.days.has(day)),
      hours: entry.from && entry.to ? compactRange(entry.from, entry.to) : undefined,
      backupOnly: entry.backup,
    }))
    .sort((a, b) => Number(a.backupOnly) - Number(b.backupOnly) || a.name.localeCompare(b.name))
}

/** One day's business hours as the practice schedule implies them: earliest start to latest end, or closed. */
export type ScheduleDayHours = { date: string; from?: string; to?: string }

const NO_NAMES: NameLookups = { providerName: () => ``, operatoryName: () => ``, serviceName: () => undefined }

/**
 * The practice's open hours per date from its schedule: the earliest start to the latest end of any
 * active provider's availability that day. Backup chairs are left out; they are not office hours.
 */
export function scheduleHoursByDate(
  records: AvailabilityRecord[],
  dates: string[],
  activeProviderIds?: Set<string>,
): ScheduleDayHours[] {
  const working = records.filter(
    (record) =>
      !record.backupAvailability &&
      (!activeProviderIds || (!!record.providerId && activeProviderIds.has(record.providerId))),
  )
  return dates.map((date) => {
    const blocks = setupBlocksForDate(working, date, NO_NAMES)
    if (blocks.length === 0) return { date }
    const from = blocks.reduce((min, block) => (sortKey(block.from) < sortKey(min) ? block.from : min), blocks[0].from)
    const to = blocks.reduce((max, block) => (sortKey(block.to) > sortKey(max) ? block.to : max), blocks[0].to)
    return { date, from, to }
  })
}

/** "07:00" and "17:30" as "7a" and "5:30p", for a calendar cell. */
export function tinyClock(time: string): string {
  return formatClock(time)
    .replace(`:00`, ``)
    .replace(/ ([AP])M$/, (_, half: string) => half.toLowerCase())
}
